import React, { useEffect } from 'react';
import { dndItemAtom, dndStore } from '@src/atoms';
import { setDndNodeItem, getDndNodeItem } from '@src/atoms';
import { removeNodeById } from '@src/scenario-editor/render-utils/remove-node.ts';
import type {CustomEdge, CustomNode} from '@src/components/custom-nodes/types';
import { recalculateNodesYPosForNodes } from '@src/scenario-editor/utils';
import { recalculateHandlePositions, unselectAllNodes } from '@src/scenario-editor/render-utils';
import { useDnD as useEditorDnD } from '@src/scenario-editor/hooks';
import { isDuplicateModifier } from './duplicate-modifier';
import type { ReactFlowInstance } from 'reactflow';
import type { CustomNodeData } from '@src/components/custom-nodes/types';

interface UseDnDProps {
  instance: ReactFlowInstance;
  nodeRef: React.RefObject<HTMLDivElement>;
  id: string;
  data: CustomNodeData
}

export const useNodeDnD = ({ instance, nodeRef, id, data }: UseDnDProps)  => {
  const isNodeDraggable = data.isDraggable ?? false;

  const { onDragEnter } = useEditorDnD({
    reactFlowInstance: instance,
    reactFlowWrapper: { current: null },
  });

  useEffect(() => {
    const onDragStart = (e: DragEvent) => {
      setDndNodeItem({
        nodes: instance.getNodes() as CustomNode[],
        edges: instance.getEdges() as CustomEdge[]
      });
      unselectAllNodes(instance);

      const withDuplicateModifier = isDuplicateModifier(e);
      const payload = JSON.stringify({
        ...data,
        type: 'GenericNode',
        id
      });
      if (e.dataTransfer) {
        e.dataTransfer.setData('application/reactflow', payload);
        e.dataTransfer.effectAllowed = withDuplicateModifier ? 'copy' : 'move';
      }

      // INFO: cloned node
      const nodeClone = nodeRef.current?.cloneNode(true) as HTMLElement;
      if (nodeClone) {
        nodeClone.style.position = 'absolute';
        nodeClone.style.top = '-9999px';
        document.body.appendChild(nodeClone);
        if (e.dataTransfer) {
          e.dataTransfer.setDragImage(nodeClone, 0, 0);
        }
      }

      if (!withDuplicateModifier) {
        if (e.currentTarget) {
          (e.currentTarget as HTMLDivElement).style.opacity = '0.4';
        }
      }

      dndStore.set(dndItemAtom, payload);
      localStorage.setItem('dndItem', payload);

      if (!withDuplicateModifier) {
        setTimeout(() => {
          removeNodeById(instance)(id);
        }, 0);
      }
      setTimeout(() => {
        const nodes = instance.getNodes() as CustomNode[];
        const edges = instance.getEdges();
        recalculateNodesYPosForNodes(nodes, edges);
        onDragEnter();
        recalculateHandlePositions(nodes, edges);
      }, 100);
    };

    const onDragEnd = (e: DragEvent) => {
      if (e.currentTarget) {
        (e.currentTarget as HTMLDivElement).style.opacity = '1';
      }
      const { nodes: initialNodes, edges: initialEdges } = getDndNodeItem() ?? {};

      if (initialNodes && initialEdges) {
        instance.setNodes(initialNodes);
        instance.setEdges(initialEdges);
        recalculateNodesYPosForNodes(initialNodes, initialEdges);
        recalculateHandlePositions(initialNodes, initialEdges);
      }
    }

    nodeRef.current?.addEventListener('dragstart', onDragStart);
    nodeRef.current?.addEventListener('dragend', onDragEnd);

    return () => {
      nodeRef.current?.removeEventListener('dragstart', onDragStart);
      nodeRef.current?.removeEventListener('dragend', onDragEnd);
    }
  }, [instance, nodeRef, data])

  return {
    isNodeDraggable,
  }
}
